import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import WarningBanner from '@/components/WarningBanner'
import { useDefaultIncompleteReportMonth } from '@/hooks/useDefaultIncompleteReportMonth'
import {
  getAllocations,
  getNetIncomeRangeDrift,
  getWaterfallJournalEntryMonthsSummary,
  proposeAllocation,
} from '@/services/waterfall'
import type {
  AllocationProposalResponse,
  AllocationResponse,
  MemberAmounts,
  NetIncomeRangeDriftResponse,
  WaterfallJeMonthsSummaryMonth,
} from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import {
  allocationAmountClassName,
  countNonCompleteMonthOptions,
  lastFullCalendarMonthId,
  periodBoundsForMonth,
  pickOldestNonCompleteMonth,
} from '@/utils/allocationFormat'
import WaterfallTabHeader from './WaterfallTabHeader'
import AllocationMonthReadiness from './AllocationMonthReadiness'
import PacYearEndClosePanel from './PacYearEndClosePanel'
import WaterfallJournalEntryCard from './WaterfallJournalEntryCard'
import type { AllocationReadinessChange } from './allocationMonthReadinessUtils'
import {
  buildRangeNetIncomeDriftMessage,
  buildRangeNetIncomeUnavailableMessage,
} from './allocationMonthReadinessUtils'
import { mergeIncomeAllocationMonthSelectOptions, monthFromIsoDate, patchIncomeAllocationMonthsSummary } from './waterfallJeStatus'
import { shouldShowPacYearEndClosePanel } from './pacYearEndCloseUtils'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const SEED_MISSING_HINT = 'Capital account seed is not configured'

function memberTotal(amounts: MemberAmounts): number {
  return MEMBERS.reduce((sum, m) => sum + Number(amounts[m]), 0)
}

function memberTransferTotal(transfers: Record<string, number>): number {
  return MEMBERS.reduce((sum, m) => sum + Number(transfers[m] ?? 0), 0)
}

function findAllocationForMonth(
  allocations: AllocationResponse[],
  month: string,
): AllocationResponse | null {
  return allocations.find((row) => monthFromIsoDate(row.period_end) === month) ?? null
}

type AllocationBridgeData = {
  beginning_capital: MemberAmounts
  contributions: MemberAmounts
  distributions: MemberAmounts
  transfers: Record<string, number>
  allocation: MemberAmounts
  ending_capital: MemberAmounts
  target_capital: MemberAmounts
  net_income: number
}

function bridgeFromLogged(row: AllocationResponse): AllocationBridgeData {
  return {
    beginning_capital: row.beginning_capital,
    contributions: row.contributions,
    distributions: row.distributions,
    transfers: row.transfers ?? {},
    allocation: row.allocation,
    ending_capital: row.ending_capital,
    target_capital: row.target_capital,
    net_income: row.net_income,
  }
}

function bridgeFromProposal(row: AllocationProposalResponse): AllocationBridgeData {
  return {
    beginning_capital: row.beginning_capital,
    contributions: row.contributions,
    distributions: row.distributions,
    transfers: row.transfers ?? {},
    allocation: row.allocation,
    ending_capital: row.ending_capital,
    target_capital: row.target_capital,
    net_income: row.net_income,
  }
}

function AllocationBridgeTable({ bridge }: { bridge: AllocationBridgeData }) {
  return (
    <div className="overflow-x-auto">
      <table className="table-forecast min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Member</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Beginning</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">+ Contrib</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">− Dist</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">± Transfers</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Allocation</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Ending</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Target</th>
          </tr>
        </thead>
        <tbody>
          {MEMBERS.map((m) => {
            const transferAmt = Number(bridge.transfers[m] ?? 0)
            return (
              <tr key={m} className="border-t border-gray-100">
                <td className="px-4 py-2 font-medium text-gray-900">{m}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {fmt.format(Number(bridge.beginning_capital[m]))}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {fmt.format(Number(bridge.contributions[m]))}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {fmt.format(Number(bridge.distributions[m]))}
                </td>
                <td
                  className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(transferAmt)}`}
                >
                  {Math.abs(transferAmt) < 0.005 ? '—' : fmt.format(transferAmt)}
                </td>
                <td
                  className={`px-4 py-2 text-right tabular-nums font-medium ${allocationAmountClassName(Number(bridge.allocation[m]))}`}
                >
                  {fmt.format(Number(bridge.allocation[m]))}
                </td>
                <td
                  className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(Number(bridge.ending_capital[m]))}`}
                >
                  {fmt.format(Number(bridge.ending_capital[m]))}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                  {fmt.format(Number(bridge.target_capital[m]))}
                </td>
              </tr>
            )
          })}
          <tr className="border-t-2 border-blue-200 bg-blue-50 font-semibold">
            <td className="px-4 py-2">Total</td>
            <td className="px-4 py-2 text-right tabular-nums">
              {fmt.format(memberTotal(bridge.beginning_capital))}
            </td>
            <td className="px-4 py-2 text-right tabular-nums">
              {fmt.format(memberTotal(bridge.contributions))}
            </td>
            <td className="px-4 py-2 text-right tabular-nums">
              {fmt.format(memberTotal(bridge.distributions))}
            </td>
            <td className="px-4 py-2 text-right tabular-nums">
              {Math.abs(memberTransferTotal(bridge.transfers)) < 0.005
                ? '—'
                : fmt.format(memberTransferTotal(bridge.transfers))}
            </td>
            <td
              className={`px-4 py-2 text-right tabular-nums ${allocationAmountClassName(bridge.net_income)}`}
            >
              {fmt.format(memberTotal(bridge.allocation))}
            </td>
            <td className="px-4 py-2 text-right tabular-nums">
              {fmt.format(memberTotal(bridge.ending_capital))}
            </td>
            <td className="px-4 py-2 text-right tabular-nums">
              {fmt.format(memberTotal(bridge.target_capital))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function AllocationTracePanel({
  trace,
  embedded = false,
}: {
  trace: Record<string, unknown>
  embedded?: boolean
}) {
  const steps = Array.isArray(trace.steps) ? trace.steps : []
  const proposal = trace.proposal as { by_member?: MemberAmounts } | undefined

  return (
    <details
      className={
        embedded ? 'border-t border-gray-200' : 'rounded-lg border border-gray-200 bg-white'
      }
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">
        How the target was computed (tier trace)
      </summary>
      <div className="border-t border-gray-100 px-4 py-3 space-y-3 text-sm text-gray-700">
        {proposal?.by_member && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Hypothetical liquidation by member
            </p>
            <ul className="mt-1 space-y-0.5">
              {MEMBERS.map((m) => (
                <li key={m} className="flex justify-between tabular-nums">
                  <span>{m}</span>
                  <span className={allocationAmountClassName(Number(proposal.by_member![m]))}>
                    {fmt.format(Number(proposal.by_member![m]))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {steps.length > 0 && (
          <div className="space-y-2">
            {steps.map((step, index) => {
              const row = step as Record<string, unknown>
              const label = typeof row.step === 'string' ? row.step : `Step ${index + 1}`
              return (
                <div key={label} className="rounded border border-gray-100 bg-gray-50 p-2">
                  <p className="font-medium text-gray-900">{label}</p>
                  {'remaining_after' in row && row.remaining_after != null && (
                    <p className="mt-1 text-xs text-gray-600">
                      Remaining after: {fmt.format(Number(row.remaining_after))}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </details>
  )
}

export default function AllocationCalculator() {
  const [monthsSummary, setMonthsSummary] = useState<WaterfallJeMonthsSummaryMonth[]>([])
  const [monthsSummaryReady, setMonthsSummaryReady] = useState(false)
  const monthSelectOptions = useMemo(
    () => mergeIncomeAllocationMonthSelectOptions(monthsSummary),
    [monthsSummary],
  )
  const setIncomeAllocationCount = useMonthCloseNavStore((s) => s.setIncomeAllocationCount)

  useEffect(() => {
    if (!monthsSummaryReady) return
    setIncomeAllocationCount(countNonCompleteMonthOptions(monthSelectOptions))
  }, [monthsSummaryReady, monthSelectOptions, setIncomeAllocationCount])

  const [selectedMonth, setSelectedMonth] = useDefaultIncompleteReportMonth(
    monthSelectOptions,
    monthsSummaryReady,
  )
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [allocations, setAllocations] = useState<AllocationResponse[]>([])
  const [blockingComplete, setBlockingComplete] = useState(false)
  const [canPostJe, setCanPostJe] = useState(false)
  const [pacPriorYearClosePosted, setPacPriorYearClosePosted] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [seedMissing, setSeedMissing] = useState(false)
  const [proposal, setProposal] = useState<AllocationProposalResponse | null>(null)
  const [readinessReloadNonce, setReadinessReloadNonce] = useState(0)
  const [rangeDrift, setRangeDrift] = useState<NetIncomeRangeDriftResponse | null>(null)
  const [rangeDriftLoaded, setRangeDriftLoaded] = useState(false)
  const proposingRef = useRef(false)

  const loggedAllocation = useMemo(
    () => (selectedMonth ? findAllocationForMonth(allocations, selectedMonth) : null),
    [allocations, selectedMonth],
  )

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await getWaterfallJournalEntryMonthsSummary('income')
      setMonthsSummary(data.months)
    } catch {
      setMonthsSummary([])
    } finally {
      setMonthsSummaryReady(true)
    }
  }, [])

  const loadAllocations = useCallback(async () => {
    try {
      const data = await getAllocations()
      setAllocations(data.allocations)
    } catch {
      setAllocations([])
    }
  }, [])

  const loadRangeDrift = useCallback(async () => {
    try {
      const data = await getNetIncomeRangeDrift()
      setRangeDrift(data)
    } catch {
      setRangeDrift({
        start_month: '2025-01',
        end_month: lastFullCalendarMonthId(),
        qbo_available: false,
        mismatches: [],
      })
    } finally {
      setRangeDriftLoaded(true)
    }
  }, [])

  useEffect(() => {
    void loadMonthsSummary()
    void loadAllocations()
    void loadRangeDrift()
  }, [loadAllocations, loadMonthsSummary, loadRangeDrift])

  useEffect(() => {
    if (!selectedMonth) return
    const bounds = periodBoundsForMonth(selectedMonth)
    setPeriodStart(bounds.periodStart)
    setPeriodEnd(bounds.periodEnd)
    setProposal(null)
    setError(null)
    setBlockingComplete(false)
    setCanPostJe(false)
    setPacPriorYearClosePosted(null)
  }, [selectedMonth])

  const handleReadinessChange = useCallback((state: AllocationReadinessChange) => {
    setBlockingComplete(state.blockingComplete)
    setCanPostJe(state.canPostJe)
    setPacPriorYearClosePosted(state.pacPriorYearClosePosted)
    if (!selectedMonth) return
    setMonthsSummary((prev) =>
      patchIncomeAllocationMonthsSummary(prev, selectedMonth, {
        checked_count: state.checkedCount,
        checklist_complete: state.allRequiredChecked,
        can_post_je: state.canPostJe,
      }),
    )
  }, [selectedMonth])

  const handleJeChanged = useCallback(() => {
    void loadMonthsSummary()
    void loadAllocations()
    setReadinessReloadNonce((nonce) => nonce + 1)
  }, [loadAllocations, loadMonthsSummary])

  const handleReset = useCallback(() => {
    setSelectedMonth(pickOldestNonCompleteMonth(monthSelectOptions))
    setProposal(null)
    setError(null)
    setSeedMissing(false)
  }, [monthSelectOptions])

  const runProposal = useCallback(async () => {
    if (proposingRef.current) return
    if (!periodStart || !periodEnd) {
      setError('Select an allocation month.')
      return
    }

    proposingRef.current = true
    setLoading(true)
    setError(null)
    setSeedMissing(false)
    void loadRangeDrift()
    try {
      const result = await proposeAllocation({
        period_start: periodStart,
        period_end: periodEnd,
      })
      setProposal(result)
    } catch (e) {
      const message = formatApiError(e, 'Failed to compute allocation')
      if (message.includes(SEED_MISSING_HINT) || message.toLowerCase().includes('seed')) {
        setSeedMissing(true)
      }
      setError(message)
    } finally {
      proposingRef.current = false
      setLoading(false)
    }
  }, [periodEnd, periodStart, loadRangeDrift])

  const showAllocateCard = Boolean(loggedAllocation) || blockingComplete
  const showCalculateActions = blockingComplete && !loggedAllocation
  const bridgeData = useMemo(() => {
    if (loggedAllocation) return bridgeFromLogged(loggedAllocation)
    if (proposal) return bridgeFromProposal(proposal)
    return null
  }, [loggedAllocation, proposal])

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Income allocation calculator"
        description="Allocate monthly net income to member capital accounts using the targeted capital account method."
      />

      {rangeDrift?.mismatches.map((mismatch) => (
        <WarningBanner
          key={mismatch.report_month}
          message={buildRangeNetIncomeDriftMessage(mismatch)}
        />
      ))}
      {rangeDriftLoaded && rangeDrift && !rangeDrift.qbo_available ? (
        <WarningBanner message={buildRangeNetIncomeUnavailableMessage(rangeDrift.end_month)} />
      ) : null}

      {selectedMonth ? (
        <AllocationMonthReadiness
          key={readinessReloadNonce}
          month={selectedMonth}
          onReadinessChange={handleReadinessChange}
          monthPicker={
            <>
              <label
                htmlFor="income-allocation-month"
                className="block text-sm font-medium text-gray-700"
              >
                Allocation month
              </label>
              <ReportMonthSelect
                id="income-allocation-month"
                options={monthSelectOptions}
                value={selectedMonth}
                onChange={(id) => id && setSelectedMonth(id)}
                disabled={!monthSelectOptions.length}
                placeholder="Select month"
                wrapperClassName="mt-1"
                className="w-[300px] max-w-full text-sm"
              />
            </>
          }
        />
      ) : (
        <div>
          <label
            htmlFor="income-allocation-month"
            className="block text-sm font-medium text-gray-700"
          >
            Allocation month
          </label>
          <ReportMonthSelect
            id="income-allocation-month"
            options={monthSelectOptions}
            value={null}
            onChange={(id) => id && setSelectedMonth(id)}
            disabled={!monthSelectOptions.length}
            placeholder="Select month"
            wrapperClassName="mt-1"
            className="w-[300px] max-w-full text-sm"
          />
        </div>
      )}

      {selectedMonth && shouldShowPacYearEndClosePanel(selectedMonth, pacPriorYearClosePosted) ? (
        <PacYearEndClosePanel
          month={selectedMonth}
          onPosted={() => setReadinessReloadNonce((nonce) => nonce + 1)}
        />
      ) : null}

      {seedMissing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Capital accounts need setup first</p>
          <p className="mt-1">
            Set each member&apos;s join date (and JR&apos;s capital contribution) on the Capital
            Account Settings tab before allocating income.
          </p>
          <Link
            to="/distribution-waterfall/settings"
            className="mt-3 inline-flex rounded-md bg-white px-3 py-1.5 text-sm font-medium text-amber-900 shadow-sm ring-1 ring-amber-200 hover:bg-amber-100"
          >
            Open Capital Account settings
          </Link>
        </div>
      )}

      {showAllocateCard ? (
        <section
          data-readonly-allow
          className="rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="container-title">Allocate net income</h3>
              {loggedAllocation ? (
                <span className="text-xs text-gray-500">
                  Allocation #{loggedAllocation.num} · {loggedAllocation.period_start} –{' '}
                  {loggedAllocation.period_end}
                </span>
              ) : null}
            </div>
            {showCalculateActions ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" gradient disabled={loading} onClick={() => void runProposal()}>
                  {loading ? 'Calculating…' : 'Calculate split'}
                </Button>
                <Button type="button" variant="outline" onClick={handleReset} disabled={loading}>
                  Reset
                </Button>
              </div>
            ) : null}
          </div>

          {(bridgeData || error || (proposal && !loggedAllocation)) ? (
            <div className="space-y-4 px-4 py-3">
              {error ? (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}

              {proposal &&
              !loggedAllocation &&
              (proposal.warnings.length > 0 || Math.abs(Number(proposal.residual_plug)) > 0.005) ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {proposal.warnings.map((w) => (
                    <p key={w}>{w}</p>
                  ))}
                  {Math.abs(Number(proposal.residual_plug)) > 0.005 ? (
                    <p>JR plug: {fmt.format(Number(proposal.residual_plug))}</p>
                  ) : null}
                </div>
              ) : null}

              {bridgeData ? <AllocationBridgeTable bridge={bridgeData} /> : null}

              {proposal && !loggedAllocation && proposal.method_note ? (
                <p className="text-xs text-gray-500">{proposal.method_note}</p>
              ) : null}

              {proposal && !loggedAllocation && proposal.trace ? (
                <AllocationTracePanel trace={proposal.trace} embedded />
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {(proposal || loggedAllocation) && selectedMonth ? (
        <WaterfallJournalEntryCard
          key={selectedMonth}
          mode="income"
          month={selectedMonth}
          showReadinessChecklist={false}
          canPostJe={canPostJe}
          onChanged={handleJeChanged}
        />
      ) : null}
    </div>
  )
}
